import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Inject, Input, OnDestroy, OnInit } from '@angular/core';
import { FormArray, FormBuilder, FormGroup } from '@angular/forms';
import {
    assignModelOptions,
    ClientManagerInstanceToken,
    ClientManagerInterface,
    ConfigConstants,
    DataMaintenanceLocationP58Model,
    DataMaintenanceUtilities,
    NotificationModel,
    RecipientListModel,
    RecipientModel,
} from '@evorait/pace-core';
import { cloneDeep } from 'lodash';
import { Subject } from 'rxjs';
import { faTrashAlt } from '@fortawesome/free-solid-svg-icons';

@Component({
    selector: 'pace-recipient-list',
    templateUrl: './recipient-list.component.html',
    styleUrls: ['./recipient-list.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class RecipientListComponent implements OnInit, OnDestroy {
    @Input()
    public model: RecipientListModel;

    @Input()
    public cseEmail = '';

    @Input()
    public parentForm: FormArray | undefined;

    @Input()
    public displaySubmitButton = false;

    @Input()
    public notificationId: NotificationModel | undefined;

    public recipientForm: FormGroup;
    public recipientFormChildren: FormArray;

    public displaySelectableOptions = true;
    private commCategory = '';
    public isNgmP58: boolean;
    private onDestroy$: Subject<void> = new Subject();
    private dataMaintenanceModel: DataMaintenanceLocationP58Model;
    private dataMaintenanceOldModel: DataMaintenanceLocationP58Model;
    public readonly faTrashAlt = faTrashAlt;

    public disableSubmit: boolean;
    public constructor(
        private readonly formBuilder: FormBuilder,
        private readonly changeDetectorRef: ChangeDetectorRef,
        @Inject(ClientManagerInstanceToken) private readonly clientManager: ClientManagerInterface,
        private readonly dataMaintenanceUtilities: DataMaintenanceUtilities,
    ) {
        this.isNgmP58 = this.clientManager.isNgmP58();
    }

    public ngOnInit(): void {
        this.recipientFormChildren = this.formBuilder.array([]);
        this.recipientForm = this.formBuilder.group({
            children: this.recipientFormChildren,
        });

        if (typeof this.notificationId !== 'undefined' && this.displaySubmitButton) {
            this.dataMaintenanceModel = new DataMaintenanceLocationP58Model(this.notificationId);
            this.dataMaintenanceOldModel = cloneDeep(this.dataMaintenanceModel);
            this.updateSubmitButtonStatus();
        }

        if (this.parentForm) {
            this.parentForm.push(this.recipientForm);
        }

        this.displaySelectableOptions = this.model.value.some((recipient) => !recipient.isDeletable);
    }

    public ngOnDestroy(): void {
        this.onDestroy$.next();
    }

    public updateRecipientStatus(index?: number): void {
        if (index) {
            if (this.notificationId && this.model.value[index]?.isDeletable) {
                this.model.value[index].isSubmitted = false;
            }
        } else {
            this.model.value.map((recipient: RecipientModel) => {
                recipient.isSubmitted = true;
            });
        }
        this.updateSubmitButtonStatus();
    }

    public updateSubmitButtonStatus(): void {
        this.disableSubmit = !this.model.value.some((recipient) => !recipient.isSubmitted);
    }

    /**
     * "Remove" a recipient by deleting the value of the model
     *
     * @param index index of the recipient to be removed
     */
    public deleteRecipient(index: number): void {
        this.model.removeModel(index);
        if (this.displaySubmitButton) {
            this.updateSubmitButtonStatus();
        }
        this.changeDetectorRef.detectChanges();
    }

    /**
     * "Add" a recipient by adding new model
     */
    public addRecipient(): void {
        this.model.addModel(
            new RecipientModel('', this.getCommCategoryFromExistingEntries(), true, true, true, false, false),
        );
        this.disableSubmit = true;
    }

    public isEmailInvalid(recipient: RecipientModel): boolean {
        return this.dataMaintenanceUtilities.isEmailInvalid(
            recipient.faxEmail.value,
            recipient.faxEmail.options.pattern,
        );
    }

    public submitToDataManagement(): void {
        this.dataMaintenanceModel.recipients.value = cloneDeep(
            this.model.value.filter((recipient) => !(recipient.isEditable && !recipient.isDeletable)),
        );

        this.dataMaintenanceUtilities.updateRecipientListInLocalStorage(cloneDeep(this.dataMaintenanceModel));

        this.updateRecipientStatus();

        this.dataMaintenanceUtilities.openMailToSubmitData(
            this.dataMaintenanceModel,
            this.dataMaintenanceOldModel,
            this.notificationId?.processIndicator === ConfigConstants.PROCESS_INDICATOR.INVITRO,
        );
    }

    public onSelectionChange(recipient: RecipientModel): void {
        recipient.faxEmail = assignModelOptions(
            recipient.faxEmail,
            {
                ...recipient.faxEmail.options,
                readonly: !recipient.isSelected.value,
            },
            recipient,
        );
        this.changeDetectorRef.detectChanges();
    }

    private getCommCategoryFromExistingEntries(): string {
        if (!this.commCategory) {
            if (this.model.value && this.model.value.length > 0) {
                this.model.value.forEach((recipient: RecipientModel) => {
                    if (recipient.commCategory.value) {
                        this.commCategory = recipient.commCategory.value;
                    }
                });
            }
        }
        return this.commCategory;
    }

    /*
    function to capture the select all value change
    */
    public onSelectAllRecipientsChange(isSelectAll: boolean): void {
        // deselect all will deselect all checkboxes, select all will select all checkboxes.
        this.model.value.forEach((recipient) => {
            if (!recipient.isDeletable && !recipient.isSelected.model.options.disabled) {
                recipient.isSelected.model.value = isSelectAll;
                recipient.isSelected = assignModelOptions(
                    recipient.isSelected,
                    {
                        ...recipient.isSelected.options,
                    },
                    recipient,
                );
                this.onSelectionChange(recipient);
            }
        });
        this.recipientFormChildren.updateValueAndValidity();
    }
}
